import React from "react"
import type { ReactNode } from "react"
import cx from "classnames"
import Icon from "../icon"
import type { IconName } from "../icon"
import { Button } from "../button"
import { LinkButton } from "../link-button"
import type { TelemetryValue } from "../event-emitter/telemetry-context"
import { cn } from "../utils"

/** Type variants available for the Note component */
export type NoteType =
  | "info"
  | "success"
  | "warning"
  | "error"
  | "generic-light"

type NoteTypeConfig = {
  bg: string
  iconColor: string
  icon: IconName
  buttonColor: string
}

const buttonStyles = ["rounded-sm p-1 px-3 text-sm font-semibold leading-6"]

export const types: Record<NoteType, NoteTypeConfig> = {
  success: {
    bg: "bg-green-100",
    iconColor: "text-green-700",
    icon: "check-circle",
    buttonColor: "bg-green-400 text-black hover:bg-green-500 focus:bg-green-500"
  },
  warning: {
    bg: "bg-orange-100",
    iconColor: "text-orange-700",
    icon: "alert-circle",
    buttonColor: "bg-orange-400 hover:bg-orange-400"
  },
  info: {
    bg: "bg-blue-100",
    iconColor: "text-blue-700",
    icon: "information-circle",
    buttonColor: "bg-blue-400 hover:bg-blue-500"
  },
  error: {
    bg: "bg-red-100",
    iconColor: "text-red-700",
    icon: "alert-triangle",
    buttonColor: "bg-red-400 hover:bg-red-500"
  },
  "generic-light": {
    bg: "bg-warm-white-100",
    iconColor: "text-gray-900",
    icon: "lightbulb",
    buttonColor: "bg-gray-900 text-white hover:bg-gray-800"
  }
}

const sizes: Record<string, string> = {
  sm: "py-2 px-4 items-center",
  lg: "p-4"
}

export type NoteAction = {
  /** Button text */
  label?: string
  /** Click handler for the action button */
  onClick?: () => void
  /** Link URL for the action button */
  href?: string
  /** Link target attribute */
  target?: string
  /** Telemetry data for the action */
  telemetry?: TelemetryValue
}

export type NoteProps = {
  /** Color/style variant of the note */
  type?: NoteType
  /** Size of the note layout */
  size?: "sm" | "lg"
  /** Title content displayed above the message. Only available on `lg` size. */
  title?: ReactNode
  /** Body text of the note */
  message?: string
  /** Action button configuration */
  action?: NoteAction
  /** Controls icon display. Set to `false` to hide, or pass a string for a custom icon name. */
  icon?: boolean | IconName
  /** Additional CSS classes */
  className?: string
  /** Custom content rendered inside the note */
  children?: ReactNode
}

/**
 * A contextual banner for displaying informational, success, warning, or error messages.
 * Supports an optional title, action button, and custom icon.
 */
function Note({
  type = "info",
  size = "lg",
  title,
  message,
  action,
  children,
  icon = true,
  className: classNameProp
}: NoteProps) {
  const className = cn(
    `rounded-lg text-sm text-black flex`,
    types[type].bg,
    sizes[size],
    classNameProp
  )

  const iconName = typeof icon === "string" ? icon : types[type].icon

  return (
    <div className={className} data-testid="note" style={{ minHeight: "48px" }}>
      {icon && (
        <Icon name={iconName} className={`${types[type].iconColor} mr-4`} />
      )}
      <div className={cn("w-full", size === "sm" && "flex items-center")}>
        {size !== "sm" && title && (
          <Title className="flex-shrink-0">{title}</Title>
        )}
        {message && (
          <p
            className={cx("text-black leading-6", size === "sm" && "flex-grow")}
          >
            {message}
          </p>
        )}
        {action &&
          (action.href ? (
            <LinkButton
              className={cn(buttonStyles, types[type].buttonColor, {
                "mt-4": size !== "sm",
                "ml-4": size === "sm"
              })}
              variant="unstyled"
              href={action.href}
              onClick={action.onClick}
              target={action.target}
              telemetry={action.telemetry}
            >
              {action.label}
            </LinkButton>
          ) : (
            <Button
              variant="unstyled"
              className={cn(buttonStyles, types[type].buttonColor, {
                "mt-4": size !== "sm",
                "ml-4": size === "sm"
              })}
              onClick={action.onClick}
              telemetry={action.telemetry}
            >
              {action.label}
            </Button>
          ))}
        {children}
      </div>
    </div>
  )
}

type TitleProps = {
  children?: ReactNode
  className?: string
}

export function Title({ children, className: classNameProp }: TitleProps) {
  const className = cn(
    "font-semibold text-sm text-black leading-6 mb-1",
    classNameProp
  )

  return <div className={className}>{children}</div>
}

export { Note }
export default Note
